/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {JobSearchFieldLabels} from '@ion/core/src/types/entities';
import {capitalizeFirstLetter, containsDelimiters, processDelimiters, toCamelCase} from '@ion/core/src/utils/StringUtil';
import AutocompleteSearch from '@ion/dls/src/components/search/AutocompleteSearch';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {isIdentifierLimitExceed, MAX_IDENTIFIER_SEARCH_LIMIT} from '@ion/features/src/search/utils';
import {resetFileFilter, resetNameFilter, setJobTabSearch} from '../../../store/actions/jobsearchengine';
import {jobSearchEngineTabsSelector} from '../../../store/selectors/jobsearchengine';
import {createSearchOptions, FilterLogicalOrFields, getDefaultFilters, getFilterByField} from './JobSearchUtils';
import {useGetFileIds} from './useGetFileIds';
import {useJobPagination} from './useJobPagination';

export enum JobTabs {
  'my-jobs' = 'My Jobs',
  'jobs'= 'Jobs'
}

export interface FilterType {
  field: string,
  keyword: string,
  disableDelete?: boolean
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    errorMessage : {
      ...dlsTypography('body3'),
      color       : dlsColor('caRed'),
      fontSize    : '12px !important',
      paddingLeft : '16px !important'
    }
  };
});

interface JobSearchWidgetProps {
  activeTab?: string;
  accountId: number;
  viewSubGroups: boolean;
  isOnlyAutoJobs?: boolean
};

export const JobSearchWidget = ({
  activeTab,
  accountId,
  viewSubGroups,
  isOnlyAutoJobs = false
}: JobSearchWidgetProps) => {
  const classes                           = useStyles();
  const searchValue                       = useRef('');
  const disableJobIdOption                = useRef(false);
  const autoJobs                          = useRef(false);
  const dispatch                          = useDispatch();
  const tabs                              = useSelector(jobSearchEngineTabsSelector);
  const [options, setOptions]             = useState([]);
  const [isValid, setIsValid]             = useState<boolean>(true);
  const [jobSearchOpen, setJobSearchOpen] = useState<boolean>(false);
  const [fileName, setFileName]           = useState('');
  const [fileIds, setFileIds]             = useState([]);
  const [filters, setFilters]             = useState<FilterType[]>(
    getDefaultFilters(tabs, activeTab, isOnlyAutoJobs));
  const {loadFileIds, isLoading}          = useGetFileIds();
  const {clearPaginationForTab}           = useJobPagination();

  useEffect(() => {
    let newFilters = getDefaultFilters(tabs, activeTab, isOnlyAutoJobs) as FilterType[];
    autoJobs.current = isOnlyAutoJobs;
    if (activeTab?.includes('jobs')) {
      if (isOnlyAutoJobs) {
        const autoJobFilter = {field: 'name', keyword: 'AJID', disableDelete: true};
        const hasAutoJob = newFilters.findIndex(({keyword}) => keyword === 'AJID') < 0;
        if (hasAutoJob) {
          newFilters = newFilters.filter(({field}) => field !== 'name');
          newFilters = [...newFilters, autoJobFilter];
        }
      } else {
        if (getFilterByField(tabs, activeTab, 'nameFilter') === 'AJID') {
          dispatch(resetNameFilter());
        }
        newFilters = newFilters?.filter(({field, keyword}) =>
          !(keyword === 'AJID' && field === 'name'));
      }
      const fileFilter = tabs.find(tab => tab.key === activeTab)?.fileName;
      if (fileFilter) {
        const ids = tabs.find(tab => tab.key === activeTab)?.fileIds;
        setFileName(fileFilter);
        setFileIds(ids);
      } else {
        setFileIds([]);
        setFileName('');
      }
    }

    setFilters(newFilters);
  }, [tabs, activeTab, isOnlyAutoJobs]);

  const getIds = async({fileName, reason, field, keyword}) => {
    await loadFileIds({
      accountId,
      includeSubGroups : viewSubGroups,
      fileName
    }, {
      onSuccess : (jobFileIds: number[]) => {
        setFileName(fileName);
        setFileIds(jobFileIds);
        setNewFilters(reason, field, keyword);
      }
    });
  };

  useEffect(() => {
    if (filters?.length) {
      const jobIdFilter = filters.filter(({field}) => field === FilterLogicalOrFields.jobId);
      const disableJobId = jobIdFilter[0]?.keyword?.split(',')?.length === MAX_IDENTIFIER_SEARCH_LIMIT;
      disableJobIdOption.current = disableJobId;
    }
    if (!isLoading && fileName && fileIds?.length) {
      dispatch(setJobTabSearch(filters, {fileName, fileIds}));
    } else {
      dispatch(setJobTabSearch(filters, {fileName: '', fileIds: []}));
    }
  }, [filters, disableJobIdOption, fileName, fileIds, isLoading]);
  
  function handleSearchChange(e: ChangeEvent<{}>, value: string) {
    setOptions([]);
    value = value.trim();
    if (value) {
      const isValid = !isIdentifierLimitExceed(value);
      setIsValid(isValid);
    }
    if (value?.length && isValid) {
      setOptions(createSearchOptions(value, activeTab, disableJobIdOption?.current, autoJobs?.current || false));
    }
  }
  function onSearchFocus() {
    setJobSearchOpen(true);
  }

  function onSearchBlur() {
    setJobSearchOpen(false);
  }

  function setNewFilters(reason, field, keyword) {
    if (reason !== 'clear') {
      setFilters((prevState) => {
        const newFilters = [...prevState];
        const newFilter = {
          field,
          keyword : field === FilterLogicalOrFields.progressStatus
            ? capitalizeFirstLetter(toCamelCase(keyword)) as string
            : field === FilterLogicalOrFields.jobId && containsDelimiters(keyword, ' ')
              ? processDelimiters(keyword, {isExact: true, joinWith: ',', trim: true})
              : keyword
        };

        const index = newFilters.findIndex(filter => filter.field === newFilter.field);

        if (index !== -1) {
          const values = `${newFilters[index].keyword},${newFilter.keyword}`;
          if (values?.split(',')?.length > MAX_IDENTIFIER_SEARCH_LIMIT) {
            setIsValid(false);
            const timeoutId = setTimeout(() => {
              setIsValid(true);
              clearInterval(timeoutId);
            }, 3000);
            return prevState;
          } else {
            newFilters[index] = {field: newFilters[index].field, keyword: values};
          }
        } else {
          newFilters.push(newFilter);
        }
        return newFilters;
      });
    } else {
      setFilters([]);
    }
  }

  function onResultSelect(
    e: ChangeEvent<{}>,
    value: {item: string, text: string},
    reason: string
  ) {
    const field = value?.item ?? '';
    let keyword = value?.text ?? '';
    if (!keyword?.endsWith('...')) {
      searchValue.current = keyword;
    } else {
      keyword = searchValue.current;
    }
    clearPaginationForTab(activeTab);
    if (field === 'filename') {
      getIds({fileName: searchValue.current, reason, field, keyword});
    } else {
      setNewFilters(reason, field, keyword);
    }
  }

  return (
    <div style={{marginBottom: 10}}>
      <ConditionalRender condition={activeTab?.includes('jobs')}>
        <AutocompleteSearch key={`${activeTab}-search`}
          searchText={JobTabs[activeTab]}
          options={options}
          isValid={isValid}
          minWidth={'40%'}
          width={'fit-content'}
          overrideTitleStyles
          filterChips={filters}
          onDelete={function(filter) {
            if (filter?.field === 'name') {
              dispatch(resetNameFilter());
            }
            if (filter?.field === 'filename') {
              dispatch(resetFileFilter());
              setFileName('');
              setFileIds([]);
            }
            if (filter?.field === FilterLogicalOrFields.jobId) {
              disableJobIdOption.current = false;
            }
            clearPaginationForTab(activeTab);
            setFilters(prevState => prevState?.filter(({field}) => !(field === filter?.field)));
          }}
          setFilters={setFilters}
          onChange={handleSearchChange}
          onFocus={onSearchFocus}
          onBlur={onSearchBlur}
          onResultSelect={onResultSelect}
          recentSearchesTitle={''}
          recentSearches={[]}
          loading={true}
          inputLength={1}
          debounceDelay={100}
          isGlobalSearchMaxLength={false}
          isCtcAdminGlobalSearch={false}
          shortPlaceholder={jobSearchOpen}
          chipFieldLabels={JobSearchFieldLabels}
          chipsEnabled
          isJobSearch />
      </ConditionalRender>
      {!isValid && (
        <div className={classes.errorMessage}>
          {translate('common:search.globalSearchLimitError', {limit: MAX_IDENTIFIER_SEARCH_LIMIT})}
        </div>
      )}
    </div>
  );
};

export default JobSearchWidget;
